import type { AssetClientContext } from "@pej/asset-client";
import { useMemo } from "react";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { env } from "@/lib/env";

/**
 * Builds the asset client's context, or returns null when this deployment has
 * no asset API configured.
 *
 * **Null is a supported state, not an error.** k3d runs no asset service, and a
 * design template is otherwise fully editable without one — so consumers render
 * the artwork surfaces as unavailable rather than throwing or hiding the whole
 * editor.
 *
 * `fetch` is injected rather than read from the global so tests need no network.
 * Note what is deliberately NOT passed: `uploadFetch`. The client defaults it to
 * the uninstrumented global because telemetry SDKs commonly wrap
 * `globalThis.fetch` and record request URLs — and the storage upload's URL
 * carries a credential, which such a wrapper would ship to a third party.
 * Passing an instrumented fetch there would undo that.
 */
export function useAssetClientContext(): AssetClientContext | null {
    const { workspaceId } = useWorkspaceContext();

    return useMemo(() => {
        const baseUrl = env.assetApiBaseUrl;
        if (!baseUrl || !workspaceId) return null;
        return {
            baseUrl,
            workspaceId,
            // These SPAs authenticate by cookie through the gateway's
            // oauth2-proxy, and a cross-origin fetch omits cookies unless asked.
            // Without this every asset call is an anonymous 401.
            fetch: (input, init) => globalThis.fetch(input, { ...init, credentials: "include" }),
        };
    }, [workspaceId]);
}
